import { useEffect } from 'react'
import { FormProvider, useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import { Box, Button, CloseButton, HStack, VStack } from '@chakra-ui/react'
import { CategoryFileList } from '@eapproval/ui'

import { Card } from '@/components/Elements'
import { Input } from '@/components/Form'
import { OfferDocumentTypes } from '@/features/offers/constants'
import useFile from '@/features/files/useFile'
import useUserManual from '@/features/userManual/useUserManual'
import useUserManualFileActions from '@/features/userManual/useUserManualFileActions'
import { UserManualType } from '@/features/userManual/userManual.enum'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { allFileTypes } from '@/utils/constants'

const CategoryRow = ({ index, remove, fileType }) => {
  const { control, getValues, setValue } = useFormContext()
  const { handleView, handleDownload } = useUserManualFileActions()
  const { useUploadFile } = useFile()
  const uploadFile = useUploadFile()
  const dispatch = useDispatch()

  const files = useWatch({ control, name: `categories.${index}.files` }) ?? []
  const name = useWatch({ control, name: `categories.${index}.name` }) ?? ''

  const handleAddFiles = async (fileList) => {
    dispatch(showLoadingOverlay())
    try {
      const filesArray = Array.from(fileList)
      const res = await uploadFile.mutateAsync({ version: 0, type: fileType, files: filesArray })
      const currentFiles = getValues(`categories.${index}.files`) ?? []
      setValue(`categories.${index}.files`, [...currentFiles, ...res.data], { shouldDirty: true })
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleRemoveFile = (file) => {
    const currentFiles = getValues(`categories.${index}.files`) ?? []
    setValue(
      `categories.${index}.files`,
      currentFiles.filter((doc) => (doc?._id || doc) !== file._id),
      { shouldDirty: true }
    )
  }

  return (
    <Box borderWidth="1px" borderRadius="md" p={3}>
      <HStack align="center" spacing={4}>
        <Input name={`categories.${index}.name`} maxW="220px" />
        <Box flex="1">
          <CategoryFileList
            files={files}
            editable
            title={name}
            allowedFileTypes={allFileTypes}
            onView={handleView}
            onDownload={handleDownload}
            onAddFiles={handleAddFiles}
            onRemoveFile={handleRemoveFile}
          />
        </Box>
        <CloseButton type="button" onClick={() => remove(index)} />
      </HStack>
    </Box>
  )
}

const UserManualCard = ({ title, userManualType = false }) => {
  const dispatch = useDispatch()
  const type = userManualType === 'external' ? UserManualType.EXTERNAL : UserManualType.INTERNAL
  const fileType =
    userManualType === 'external' ? OfferDocumentTypes.EXTERNAL_USER_MANUAL : OfferDocumentTypes.INTERNAL_USER_MANUAL

  const { useFetchUserManualByType, useUpdateUserManual } = useUserManual()
  const { data: userManual } = useFetchUserManualByType(type)
  const updateUserManual = useUpdateUserManual()

  const methods = useForm({ defaultValues: { categories: [] } })
  const { control, handleSubmit, reset } = methods
  const { fields, append, remove } = useFieldArray({ control, name: 'categories' })

  useEffect(() => {
    if (userManual?.categories) {
      reset({ categories: userManual.categories })
    }
  }, [userManual, reset])

  const onSubmit = async (data) => {
    if (!userManual?._id) {
      toast.error('Missing User Manual Id')
      return
    }

    const categories = data.categories.map((category) => ({ ...category, name: (category.name ?? '').trim() }))

    if (categories.some((category) => !category.name)) {
      toast.error('Category name is required')
      return
    }

    const names = categories.map((category) => category.name)
    if (new Set(names).size !== names.length) {
      toast.error('Category names must be unique')
      return
    }

    try {
      dispatch(showLoadingOverlay())

      await updateUserManual.mutateAsync({
        id: userManual._id,
        data: {
          categories: categories.map((category) => ({
            name: category.name,
            files: (category.files ?? []).map((file) => file?._id || file),
          })),
        },
      })
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  return (
    <Card
      title={title}
      rightElement={
        <Button type="button" colorScheme="green" onClick={() => append({ name: '', files: [] })}>
          Add Category
        </Button>
      }
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box p={5}>
            <VStack align="stretch" spacing={4}>
              {fields.map((field, index) => (
                <CategoryRow key={field.id} index={index} remove={remove} fileType={fileType} />
              ))}
            </VStack>
            <Button mt="6" colorScheme="blue" type="submit">
              Save
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Card>
  )
}

export default UserManualCard
